import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useAuth } from '../utils/AuthContext';
import { useCRM } from '../utils/CRMContext';
import { canViewOrgWideEmployeeBenefits } from '../utils/employeeBenefitsAccess';
import {
  type EmployeeBenefitsSettingsDoc,
  type EmployeePayrollSnapshot,
  type LeaveBucketStored,
  defaultEmployeeBenefitsSettings,
  fetchAllPayrollSnapshots,
  fetchOwnPayrollSnapshot,
  savePayrollScheduleFields,
  subscribeEmployeeBenefitsSettings,
  saveEmployeeLeaveProfileAdmin,
  applyLeaveUsedForPeriodAdmin,
} from '../utils/peopleHubFirestore';
import {
  LEAVE_BUCKET_KEYS,
  emptyLeaveBuckets,
  listPayDatesInRange,
  type LeaveBucketKey,
  type PayrollSchedule,
  ymdInTimeZone,
} from '../utils/leaveAccrual';
import { addDays } from 'date-fns';
import { ClipboardList } from 'lucide-react';
import '../components/pm/pm-shared.css';
import './OperationsHub.css';
import './PeopleHubPage.css';

const BUCKET_LABEL: Record<LeaveBucketKey, string> = {
  sick: 'Sick',
  vacation: 'Vacation',
  paidTimeOff: 'Paid time off',
  unpaidTimeOff: 'Unpaid time off',
};

function bucketDisplay(snapshot: EmployeePayrollSnapshot | null, k: LeaveBucketKey): string {
  const b = snapshot?.leaveBuckets?.[k];
  if (!b || typeof b.available !== 'number') return '—';
  return String(b.available);
}

function mergeBucketsFromSnapshot(s: EmployeePayrollSnapshot | null): Record<
  LeaveBucketKey,
  { available: number; accrualPerPayRun: number }
> {
  const out = emptyLeaveBuckets();
  if (!s?.leaveBuckets) return out;
  for (const k of LEAVE_BUCKET_KEYS) {
    const b = s.leaveBuckets[k];
    if (b && typeof b.available === 'number') out[k].available = b.available;
    if (b && typeof b.accrualPerPayRun === 'number') out[k].accrualPerPayRun = b.accrualPerPayRun;
  }
  return out;
}

const PeopleBenefitsPage: React.FC = () => {
  const { user } = useAuth();
  const { crmUsers } = useCRM();
  const [settings, setSettings] = useState<EmployeeBenefitsSettingsDoc>(defaultEmployeeBenefitsSettings());
  const [snapshots, setSnapshots] = useState<(EmployeePayrollSnapshot & { id: string })[]>([]);
  const [ownSnapshot, setOwnSnapshot] = useState<(EmployeePayrollSnapshot & { id: string }) | null>(null);
  const [busy, setBusy] = useState(false);
  const [statusMsg, setStatusMsg] = useState<string | null>(null);

  const [scheduleType, setScheduleType] = useState<'biweekly' | 'dates'>('biweekly');
  const [anchorPayDate, setAnchorPayDate] = useState('');
  const [payDatesText, setPayDatesText] = useState('');
  const [defaultAccrualDraft, setDefaultAccrualDraft] = useState(() => emptyLeaveBuckets());

  const [profileUid, setProfileUid] = useState<string | null>(null);
  const [profileDraft, setProfileDraft] = useState(() => emptyLeaveBuckets());

  const [usedUid, setUsedUid] = useState<string | null>(null);
  const [usedPayDate, setUsedPayDate] = useState(() => ymdInTimeZone(new Date(), 'America/Chicago'));
  const [usedDraft, setUsedDraft] = useState<Record<LeaveBucketKey, number>>({
    sick: 0,
    vacation: 0,
    paidTimeOff: 0,
    unpaidTimeOff: 0,
  });

  const me = useMemo(() => crmUsers.find(u => u.uid === user?.id), [crmUsers, user?.id]);
  const approvalDesignations = me?.designations ?? user?.designations;
  const canOrg = useMemo(
    () => canViewOrgWideEmployeeBenefits({ authRole: user?.role, designations: approvalDesignations }),
    [user?.role, approvalDesignations],
  );

  useEffect(() => {
    const unsub = subscribeEmployeeBenefitsSettings(
      d => {
        setSettings(d);
        if (d.payrollSchedule?.type === 'biweekly') {
          setScheduleType('biweekly');
          setAnchorPayDate(d.payrollSchedule.anchorPayDate || '');
          setPayDatesText('');
        } else if (d.payrollSchedule?.type === 'dates') {
          setScheduleType('dates');
          setPayDatesText((d.payrollSchedule.payDates || []).join('\n'));
          setAnchorPayDate('');
        }
        const da = d.defaultAccrualPerPayRun;
        setDefaultAccrualDraft(prev => {
          const n = emptyLeaveBuckets();
          for (const k of LEAVE_BUCKET_KEYS) {
            n[k].accrualPerPayRun = typeof da?.[k] === 'number' ? da[k]! : prev[k].accrualPerPayRun;
          }
          return n;
        });
      },
      e => console.error('[PeopleBenefits]', e),
    );
    return unsub;
  }, []);

  const loadSnapshots = useCallback(async () => {
    if (!user?.id) return;
    try {
      if (canOrg) {
        const rows = await fetchAllPayrollSnapshots();
        setSnapshots(rows);
        setOwnSnapshot(null);
      } else {
        const one = await fetchOwnPayrollSnapshot(user.id);
        setOwnSnapshot(one);
        setSnapshots([]);
      }
    } catch (e) {
      console.error('[PeopleBenefits] snapshots', e);
    }
  }, [user?.id, canOrg]);

  useEffect(() => {
    void loadSnapshots();
  }, [loadSnapshots]);

  const snapshotById = useMemo(() => {
    const m = new Map<string, EmployeePayrollSnapshot & { id: string }>();
    for (const s of snapshots) m.set(s.id, s);
    return m;
  }, [snapshots]);

  const internalPeople = useMemo(
    () => crmUsers.filter(u => u.role === 'employee' || u.role === 'admin').sort((a, b) => {
      const an = (a.displayName || a.email || '').toLowerCase();
      const bn = (b.displayName || b.email || '').toLowerCase();
      return an.localeCompare(bn);
    }),
    [crmUsers],
  );

  const savePayrollSchedule = async () => {
    setBusy(true);
    setStatusMsg(null);
    try {
      let payrollSchedule: PayrollSchedule;
      if (scheduleType === 'biweekly') {
        if (!/^\d{4}-\d{2}-\d{2}$/.test(anchorPayDate.trim())) {
          alert('Enter anchor pay date as yyyy-mm-dd.');
          return;
        }
        payrollSchedule = { type: 'biweekly', anchorPayDate: anchorPayDate.trim() };
      } else {
        const lines = payDatesText
          .split(/\n/)
          .map(l => l.trim())
          .filter(Boolean)
          .filter(l => /^\d{4}-\d{2}-\d{2}$/.test(l));
        if (lines.length === 0) {
          alert('Add at least one pay date (yyyy-mm-dd), one per line.');
          return;
        }
        payrollSchedule = { type: 'dates', payDates: Array.from(new Set(lines)).sort() };
      }
      const defaultAccrualPerPayRun: Partial<Record<LeaveBucketKey, number>> = {};
      for (const k of LEAVE_BUCKET_KEYS) {
        defaultAccrualPerPayRun[k] = defaultAccrualDraft[k].accrualPerPayRun;
      }
      await savePayrollScheduleFields({ payrollSchedule, defaultAccrualPerPayRun });
      setStatusMsg('Payroll schedule saved.');
    } catch (e) {
      console.error('[PeopleBenefits] savePayrollScheduleFields', e);
      const fe = e as { code?: string; message?: string };
      alert(`${fe.code ?? 'error'}: ${fe.message ?? String(e)}`);
    } finally {
      setBusy(false);
    }
  };

  const openProfile = (uid: string) => {
    const snap = snapshotById.get(uid);
    const merged = mergeBucketsFromSnapshot(snap ?? null);
    const defs = settings.defaultAccrualPerPayRun;
    for (const k of LEAVE_BUCKET_KEYS) {
      if (!snap?.leaveBuckets?.[k] && typeof defs?.[k] === 'number') {
        merged[k].accrualPerPayRun = defs[k]!;
      }
    }
    setProfileDraft(merged);
    setProfileUid(uid);
  };

  const saveProfile = async () => {
    if (!profileUid) return;
    const target = crmUsers.find(u => u.uid === profileUid);
    setBusy(true);
    setStatusMsg(null);
    try {
      await saveEmployeeLeaveProfileAdmin({
        targetUid: profileUid,
        leaveBuckets: profileDraft,
        email: target?.email ?? null,
        displayName: (target?.displayName || '').trim() || null,
      });
      setProfileUid(null);
      setStatusMsg('Leave profile saved.');
      await loadSnapshots();
    } catch (e) {
      console.error('[PeopleBenefits] saveEmployeeLeaveProfile', e);
      const fe = e as { code?: string; message?: string };
      alert(`${fe.code ?? 'error'}: ${fe.message ?? String(e)}`);
    } finally {
      setBusy(false);
    }
  };

  const openUsed = (uid: string) => {
    setUsedUid(uid);
    setUsedPayDate(ymdInTimeZone(new Date(), 'America/Chicago'));
    setUsedDraft({ sick: 0, vacation: 0, paidTimeOff: 0, unpaidTimeOff: 0 });
  };

  const submitUsed = async () => {
    if (!usedUid) return;
    setBusy(true);
    setStatusMsg(null);
    try {
      await applyLeaveUsedForPeriodAdmin({
        targetUid: usedUid,
        payDate: usedPayDate.trim(),
        usedHours: usedDraft,
      });
      setUsedUid(null);
      setStatusMsg('Used hours applied.');
      await loadSnapshots();
    } catch (e) {
      console.error('[PeopleBenefits] applyLeaveUsedForPeriod', e);
      const fe = e as { code?: string; message?: string };
      alert(`${fe.code ?? 'error'}: ${fe.message ?? String(e)}`);
    } finally {
      setBusy(false);
    }
  };

  const previewPayDates = useMemo(() => {
    const sch = settings.payrollSchedule;
    if (!sch) return [];
    const start = new Date();
    const end = addDays(start, 120);
    return listPayDatesInRange(sch, start, end).slice(0, 8);
  }, [settings.payrollSchedule]);

  if (!user) return null;

  const displaySnapshot = canOrg ? null : ownSnapshot;

  return (
    <div className="people-hub-route-root">
      <div className="ops-hub-toolbar">
        <div className="ops-hub-toolbar-left">
          <div>
            <h2 className="ops-hub-title">Benefits &amp; payroll</h2>
            <p className="people-hub-subtitle">
              Company benefits and internal leave balances. Accrual runs automatically on each pay date once a profile
              and org schedule are set.
            </p>
          </div>
        </div>
      </div>

      <div className="people-hub-body pm-scroll">
        <div className="people-hub-panel people-hub-benefits-page-panel">
          <h3 className="people-hub-panel-title">Benefits</h3>
          <div className="people-hub-benefit-cards">
            <div className="people-hub-benefit-card">
              <div className="people-hub-benefit-label">SEP IRA (company)</div>
              <div className="people-hub-benefit-value">
                {settings.sepIraContributionPercent != null ? `${settings.sepIraContributionPercent}%` : '—'}
              </div>
              <p className="people-hub-benefit-note">Set by an admin in Settings → Admin.</p>
            </div>
            <div className="people-hub-benefit-card people-hub-benefit-card-wide">
              <div className="people-hub-benefit-label">Health insurance reimbursement</div>
              <p className="people-hub-benefit-prose">
                {settings.healthInsuranceReimbursementSummary?.trim()
                  ? settings.healthInsuranceReimbursementSummary
                  : 'Details will appear here once an admin adds a summary in Settings.'}
              </p>
            </div>
          </div>

          {canOrg && (
            <>
              <h3 className="people-hub-panel-title people-hub-panel-title-spaced">
                <ClipboardList size={16} aria-hidden /> Payroll schedule &amp; default accrual
              </h3>
              <p className="people-hub-muted people-hub-tight-top">
                Pay dates control the team calendar ($) and when scheduled accrual runs (Chicago mornings). Defaults
                pre-fill new employee accrual rates.
              </p>
              <div className="people-hub-org-schedule">
                <div className="people-hub-radio-row">
                  <label className="people-hub-radio">
                    <input
                      type="radio"
                      name="payroll-schedule-type"
                      checked={scheduleType === 'biweekly'}
                      onChange={() => setScheduleType('biweekly')}
                    />{' '}
                    Biweekly from anchor
                  </label>
                  <label className="people-hub-radio">
                    <input
                      type="radio"
                      name="payroll-schedule-type"
                      checked={scheduleType === 'dates'}
                      onChange={() => setScheduleType('dates')}
                    />{' '}
                    Explicit pay dates
                  </label>
                </div>
                {scheduleType === 'biweekly' ? (
                  <div className="ops-modal-row people-hub-tight-top">
                    <label className="ops-modal-label" htmlFor="people-anchor-pay">
                      Anchor pay date (yyyy-mm-dd)
                    </label>
                    <input
                      id="people-anchor-pay"
                      className="ops-modal-input"
                      type="date"
                      value={anchorPayDate}
                      onChange={e => setAnchorPayDate(e.target.value)}
                    />
                  </div>
                ) : (
                  <div className="ops-modal-section people-hub-tight-top">
                    <label className="ops-modal-label" htmlFor="people-pay-dates">
                      Pay dates (one yyyy-mm-dd per line)
                    </label>
                    <textarea
                      id="people-pay-dates"
                      className="ops-modal-textarea"
                      rows={5}
                      value={payDatesText}
                      onChange={e => setPayDatesText(e.target.value)}
                    />
                  </div>
                )}
                <h4 className="people-hub-subpanel-title">Default accrual per pay run (hours)</h4>
                <div className="people-hub-accrual-grid">
                  {LEAVE_BUCKET_KEYS.map(k => (
                    <div key={k} className="ops-modal-row">
                      <label className="ops-modal-label" htmlFor={`def-accr-${k}`}>
                        {BUCKET_LABEL[k]}
                      </label>
                      <input
                        id={`def-accr-${k}`}
                        className="ops-modal-input"
                        type="number"
                        step="0.01"
                        value={defaultAccrualDraft[k].accrualPerPayRun}
                        onChange={e =>
                          setDefaultAccrualDraft(d => ({
                            ...d,
                            [k]: { ...d[k], accrualPerPayRun: e.target.value === '' ? 0 : Number(e.target.value) },
                          }))
                        }
                      />
                    </div>
                  ))}
                </div>
                <div className="people-hub-inline-actions people-hub-tight-top">
                  <button
                    type="button"
                    className="pm-btn pm-btn-primary"
                    disabled={busy}
                    onClick={() => void savePayrollSchedule()}
                  >
                    Save payroll schedule
                  </button>
                </div>
                {previewPayDates.length > 0 && (
                  <p className="people-hub-muted people-hub-tight-top">
                    Next pay dates (preview): {previewPayDates.join(', ')}
                  </p>
                )}
              </div>
            </>
          )}

          <h3 className="people-hub-panel-title people-hub-panel-title-spaced">
            <ClipboardList size={16} aria-hidden /> Leave balances (available hours)
          </h3>
          {statusMsg && (
            <p className="people-hub-muted people-hub-tight-top" role="status">
              {statusMsg}
            </p>
          )}

          {canOrg ? (
            <div className="people-hub-table-wrap pm-scroll">
              <table className="people-hub-table">
                <thead>
                  <tr>
                    <th>Employee</th>
                    {LEAVE_BUCKET_KEYS.map(k => (
                      <th key={k}>{BUCKET_LABEL[k]}</th>
                    ))}
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {internalPeople.map(p => {
                    const s = snapshotById.get(p.uid);
                    return (
                      <tr key={p.uid}>
                        <td>{p.displayName || p.email || p.uid}</td>
                        {LEAVE_BUCKET_KEYS.map(k => (
                          <td key={k}>{bucketDisplay(s ?? null, k)}</td>
                        ))}
                        <td>
                          <button type="button" className="ops-hide-completed-btn" onClick={() => openProfile(p.uid)}>
                            Profile
                          </button>{' '}
                          <button
                            type="button"
                            className="ops-hide-completed-btn"
                            onClick={() => openUsed(p.uid)}
                            disabled={!s?.leaveBuckets}
                            title={!s?.leaveBuckets ? 'Save a leave profile first' : undefined}
                          >
                            Post used
                          </button>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          ) : (
            <div className="people-hub-benefit-card">
              <div className="people-hub-benefit-label">Your available hours</div>
              {!displaySnapshot?.leaveBuckets ? (
                <p className="people-hub-muted">No leave profile configured yet.</p>
              ) : (
                <div className="people-hub-kv-grid">
                  {LEAVE_BUCKET_KEYS.map(k => {
                    const b = displaySnapshot.leaveBuckets?.[k] as LeaveBucketStored | undefined;
                    return (
                      <div key={k} className="people-hub-kv">
                        <span>{BUCKET_LABEL[k]}</span>
                        <strong>{typeof b?.available === 'number' ? b.available : '—'}</strong>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          )}
        </div>
      </div>

      {profileUid && (
        <div className="people-hub-modal-overlay" role="presentation" onClick={() => !busy && setProfileUid(null)}>
          <div
            className="people-hub-modal"
            role="dialog"
            aria-modal="true"
            aria-labelledby="people-profile-title"
            onClick={e => e.stopPropagation()}
          >
            <h3 id="people-profile-title" className="people-hub-modal-title">
              Leave profile
            </h3>
            <p className="people-hub-muted people-hub-tight-top">
              Set starting <strong>available</strong> hours and fixed <strong>accrual per pay run</strong> (added
              automatically each pay date). Available may be <strong>negative</strong> (for example, advance or
              correction).
            </p>
            <div className="people-hub-profile-table">
              <div className="people-hub-profile-head">
                <span />
                <span>Available (h)</span>
                <span>Accrual / pay run (h)</span>
              </div>
              {LEAVE_BUCKET_KEYS.map(k => (
                <div key={k} className="people-hub-profile-row">
                  <span>{BUCKET_LABEL[k]}</span>
                  <input
                    type="number"
                    step="any"
                    className="ops-modal-input"
                    value={profileDraft[k].available}
                    onChange={e =>
                      setProfileDraft(d => ({
                        ...d,
                        [k]: { ...d[k], available: e.target.value === '' ? 0 : Number(e.target.value) },
                      }))
                    }
                  />
                  <input
                    type="number"
                    step="any"
                    className="ops-modal-input"
                    value={profileDraft[k].accrualPerPayRun}
                    onChange={e =>
                      setProfileDraft(d => ({
                        ...d,
                        [k]: { ...d[k], accrualPerPayRun: e.target.value === '' ? 0 : Number(e.target.value) },
                      }))
                    }
                  />
                </div>
              ))}
            </div>
            <div className="people-hub-modal-actions">
              <button type="button" className="pm-btn pm-btn-ghost" disabled={busy} onClick={() => setProfileUid(null)}>
                Cancel
              </button>
              <button type="button" className="pm-btn pm-btn-primary" disabled={busy} onClick={() => void saveProfile()}>
                Save profile
              </button>
            </div>
          </div>
        </div>
      )}

      {usedUid && (
        <div className="people-hub-modal-overlay" role="presentation" onClick={() => !busy && setUsedUid(null)}>
          <div
            className="people-hub-modal"
            role="dialog"
            aria-modal="true"
            aria-labelledby="people-used-title"
            onClick={e => e.stopPropagation()}
          >
            <h3 id="people-used-title" className="people-hub-modal-title">
              Post used hours (pay period)
            </h3>
            <p className="people-hub-muted people-hub-tight-top">
              Subtracts used hours from available for the selected pay date. Re-saving replaces the prior posting for
              that date.
            </p>
            <div className="ops-modal-row">
              <label className="ops-modal-label" htmlFor="people-used-pay">
                Pay date
              </label>
              <input
                id="people-used-pay"
                className="ops-modal-date"
                type="date"
                value={usedPayDate}
                onChange={e => setUsedPayDate(e.target.value)}
              />
            </div>
            {LEAVE_BUCKET_KEYS.map(k => (
              <div key={k} className="ops-modal-row">
                <label className="ops-modal-label" htmlFor={`used-${k}`}>
                  Used — {BUCKET_LABEL[k]} (h)
                </label>
                <input
                  id={`used-${k}`}
                  className="ops-modal-input"
                  type="number"
                  step="0.01"
                  value={usedDraft[k]}
                  onChange={e =>
                    setUsedDraft(d => ({ ...d, [k]: e.target.value === '' ? 0 : Number(e.target.value) }))
                  }
                />
              </div>
            ))}
            <div className="people-hub-modal-actions">
              <button type="button" className="pm-btn pm-btn-ghost" disabled={busy} onClick={() => setUsedUid(null)}>
                Cancel
              </button>
              <button type="button" className="pm-btn pm-btn-primary" disabled={busy} onClick={() => void submitUsed()}>
                Apply used
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default PeopleBenefitsPage;
